/**
 * The two outputs that leave the app: the `Recommendations` Google Doc,
 * rewritten after every analysis, and the email digest, sent after a run that
 * filed something. Both are built here as HTML from what is already in the
 * Sheet; nothing new is asked of Claude. See ARCHITECTURE.md, Recommendations
 * Doc and Digest.
 */
import { GROUP_LABEL, type Snapshot } from './analysis'
import type { Finding, Saved } from './advice'
import type { Overdue } from './staleness'
import type { FileProgress } from './pipeline'
import type { Row } from './google/sheets'
import { RECOMMENDATIONS_DOC, writeDoc } from './google/drive'
import { sendMail } from './google/gmail'

export const DOC_NAME = RECOMMENDATIONS_DOC
export const APP_URL = 'https://financebuddy.net'

const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const pct = (n: number) => `${Math.round(n * 100)}%`

export function escape(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
}

/** A word and an icon for each tone, so nothing depends on colour. */
const TONE: Record<Finding['tone'], string> = { strength: '▲ Strength', concern: '! Concern', note: '• Note' }
const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 }

/** Open recommendations, most important first. */
export function openRecommendations(recs: Row[]): Row[] {
  return recs
    .filter((r) => (r.status ?? 'new') === 'new')
    .sort((a, b) => (PRIORITY_RANK[String(a.priority)] ?? 3) - (PRIORITY_RANK[String(b.priority)] ?? 3))
}

function value(r: Row): string {
  const v = Number(r.est_annual_value)
  return r.est_annual_value !== null && r.est_annual_value !== '' && Number.isFinite(v) && v > 0 ? ` · about ${whole.format(v)} a year` : ''
}

function overdueLine(o: Overdue): string {
  return `<b>${escape(o.name)}</b>: the last one ended ${o.last_end}, ${o.days_since} days ago${o.missed > 1 ? `; about ${o.missed} are missing` : ''}.`
}

export interface DocInput {
  saved: Saved
  snapshot: Snapshot
  overdue: Overdue[]
  sheetUrl: string
}

/** The Recommendations Doc. Drive converts this HTML to a Google Doc on upload. */
export function docHtml({ saved, snapshot, overdue, sheetUrl }: DocInput): string {
  const t = snapshot.totals
  const cf = snapshot.cash_flow
  const open = openRecommendations(saved.recommendations)
  const closed = saved.recommendations.filter((r) => r.status === 'done' || r.status === 'dismissed')
  const out: string[] = []

  out.push(`<h1>Finance Buddy recommendations</h1>`)
  out.push(
    `<p>Analysis of ${saved.run_at.slice(0, 10)}, from statements up to ${snapshot.as_of ?? '—'}. ` +
      `This document is rewritten after every analysis; mark recommendations done or dismissed in <a href="${APP_URL}">Finance Buddy</a> ` +
      `or in the <a href="${escape(sheetUrl)}">spreadsheet</a>, not here.</p>`,
  )

  out.push(`<h2>Position</h2>`)
  out.push('<table border="1" cellpadding="6" style="border-collapse:collapse">')
  const stat = (label: string, figure: string, note: string) => `<tr><td><b>${label}</b></td><td>${figure}</td><td>${note}</td></tr>`
  out.push(stat('Net worth', whole.format(t.net), `${whole.format(t.assets)} assets − ${whole.format(t.debts)} owed`))
  out.push(stat('Invested', whole.format(t.invested), `${pct(t.invested / (t.assets || 1))} of assets`))
  out.push(stat('Cash', whole.format(t.cash), cf.emergency_fund_months === null ? 'no spending data yet' : `${cf.emergency_fund_months} months of spending`))
  out.push(stat('Savings rate', cf.savings_rate === null ? '—' : pct(cf.savings_rate), cf.income ? `${whole.format(cf.income)} in − ${whole.format(cf.spending)} out` : 'no income in the statements'))
  out.push('</table>')
  if (snapshot.groups.length) {
    out.push('<p>')
    out.push(snapshot.groups.map((g) => `${escape(GROUP_LABEL[g.group])}: ${whole.format(g.total)}`).join(' · '))
    out.push('</p>')
  }

  if (overdue.length) {
    out.push(`<h2>Statements to upload</h2><ul>`)
    for (const o of overdue) out.push(`<li>${overdueLine(o)}</li>`)
    out.push('</ul>')
  }

  out.push(`<h2>Review</h2><p>${escape(saved.overview)}</p>`)
  if (saved.findings.length) {
    out.push('<ul>')
    for (const f of saved.findings) out.push(`<li><b>${TONE[f.tone]}: ${escape(f.title)}.</b> ${escape(f.detail)}</li>`)
    out.push('</ul>')
  }

  out.push(`<h2>Recommendations</h2>`)
  if (!open.length) out.push('<p>None open.</p>')
  open.forEach((r, i) => {
    out.push(`<h3>${i + 1}. ${escape(r.title)}</h3>`)
    out.push(`<p><b>${escape(String(r.priority ?? '').toUpperCase())} priority</b> · ${escape(r.area)}${value(r)}</p>`)
    out.push(`<p><b>Next step:</b> ${escape(r.next_step)}</p>`)
    out.push(`<p>${escape(r.detail)}</p>`)
  })

  if (closed.length) {
    out.push(`<h2>Already handled</h2><ul>`)
    for (const r of closed) out.push(`<li>${r.status === 'done' ? '✓ Done' : '– Dismissed'}: ${escape(r.title)}</li>`)
    out.push('</ul>')
  }

  out.push(`<p><i>Written by Finance Buddy with Claude from your own statements. It is not advice from a licensed adviser; check anything consequential with one.</i></p>`)
  return `<!doctype html><html><head><meta charset="utf-8"><title>${DOC_NAME}</title></head><body>${out.join('\n')}</body></html>`
}

export interface DigestInput {
  today: string
  files: FileProgress[]
  overdue: Overdue[]
  /** The analysis run after these files, if it finished. */
  saved?: Saved
  analysisError?: string
  docUrl?: string
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function digestSubject({ today, files }: DigestInput): string {
  const filed = files.filter((f) => f.status === 'filed').length
  const review = files.filter((f) => f.status === 'review').length
  const parts = [filed && `${plural(filed, 'document')} filed`, review && `${review} to review`].filter(Boolean)
  return `Finance Buddy ${today}: ${parts.join(', ') || 'nothing new filed'}`
}

/** The email after a run. Inline styles only: mail clients drop stylesheets. */
export function digestHtml(input: DigestInput): string {
  const { files, overdue, saved, analysisError, docUrl } = input
  const link = (href: string | undefined, text: string) => (href ? `<a href="${escape(href)}" style="color:#1a1a1a">${escape(text)}</a>` : escape(text))
  const section = (title: string) => `<h2 style="font-size:15px;margin:24px 0 8px;padding-top:12px;border-top:1px solid #d4d4d4">${title}</h2>`
  const out: string[] = []

  out.push(`<p style="font-family:ui-monospace,Menlo,monospace;font-size:16px;margin:0 0 16px">finance<span style="color:#c1121f">buddy</span></p>`)

  const groups: [FileProgress['status'], string][] = [
    ['filed', '✓ Filed'],
    ['review', '! Needs review'],
    ['duplicate', '= Set aside as duplicates'],
    ['error', '✕ Could not read'],
  ]
  for (const [status, title] of groups) {
    const list = files.filter((f) => f.status === status)
    if (!list.length) continue
    out.push(section(`${title} (${list.length})`))
    out.push('<ul style="padding-left:20px;margin:0">')
    for (const f of list) out.push(`<li style="margin:4px 0">${link(f.link, f.name)}${f.detail ? ` <span style="color:#595959">— ${escape(f.detail)}</span>` : ''}</li>`)
    out.push('</ul>')
  }

  if (overdue.length) {
    out.push(section('Statements to upload'))
    out.push('<ul style="padding-left:20px;margin:0">')
    for (const o of overdue) out.push(`<li style="margin:4px 0">${overdueLine(o)}</li>`)
    out.push('</ul>')
  }

  if (saved) {
    out.push(section('Review'))
    out.push(`<p style="margin:0">${escape(saved.overview)}</p>`)
    const top = openRecommendations(saved.recommendations).filter((r) => r.created_at === saved.run_at).slice(0, 5)
    if (top.length) {
      out.push(section('Top recommendations'))
      out.push('<ol style="padding-left:20px;margin:0">')
      for (const r of top) {
        out.push(
          `<li style="margin:8px 0"><b>${escape(r.title)}</b> <span style="color:#595959">(${escape(r.priority)} priority${value(r)})</span><br>` +
            `Next step: ${escape(r.next_step)}</li>`,
        )
      }
      out.push('</ol>')
    }
  } else if (analysisError) {
    out.push(section('Review'))
    out.push(`<p style="margin:0">✕ The analysis did not finish: ${escape(analysisError)}. Open Finance Buddy and press Re-analyse.</p>`)
  }

  out.push(`<p style="margin-top:24px;padding-top:12px;border-top:1px solid #d4d4d4;color:#595959">`)
  out.push([link(APP_URL, 'Open Finance Buddy'), docUrl && link(docUrl, 'Full recommendations')].filter(Boolean).join(' · '))
  out.push('</p>')

  return `<!doctype html><html><body style="font-family:Inter,-apple-system,Segoe UI,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#1a1a1a;max-width:640px">${out.join('\n')}</body></html>`
}

/** UTF-8 text to base64, which `btoa` alone cannot do. */
function base64(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

/** An RFC 2822 message, base64url-encoded as the Gmail API wants it. */
export function mimeMessage(to: string, subject: string, html: string): string {
  const body = base64(html).replace(/.{76}/g, '$&\r\n')
  const message = [
    `To: ${to}`,
    `Subject: =?UTF-8?B?${base64(subject)}?=`,
    'MIME-Version: 1.0',
    'Content-Type: text/html; charset=UTF-8',
    'Content-Transfer-Encoding: base64',
    '',
    body,
  ].join('\r\n')
  return base64(message).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Rewrites the Doc in the Finance Buddy folder and returns its link. */
export async function publishDoc(folderId: string, input: DocInput): Promise<string | undefined> {
  return (await writeDoc(folderId, DOC_NAME, docHtml(input))).webViewLink
}

/** Emails the digest to the signed-in account. */
export async function sendDigest(to: string, input: DigestInput): Promise<void> {
  await sendMail(mimeMessage(to, digestSubject(input), digestHtml(input)))
}
